import isNil from 'lodash/isNil'
import find from 'lodash/find'
import React from 'react'
import { isOnAccountTerm, OrderPaymentStatus } from '@ztechapps/order.common'
import { formatMoney } from '@ztechapps/common.utils'
import { HorizontalStack, ShowIf, StatusColorBadge, findStatus } from '@ztechapps/webui.util'
import { OrderPaymentStatusEnum, PaymentStatusEnum } from '@ztechapps/common.contract'

export type OrderPaymentStatusBadgeProps = {
  paymentStatus: OrderPaymentStatusEnum
  balance?: number
  payments?: { status: string }[]
  // The order's stored payment-term value (JSON rule). When it parses to a credit
  // term and the order is still open, an "On Account" pill renders alongside.
  paymentTerms?: string
}
export const OrderPaymentStatusBadge = ({
  paymentStatus,
  balance,
  payments,
  paymentTerms,
}: OrderPaymentStatusBadgeProps) => {
  const orderPaymentStatus = findStatus(OrderPaymentStatus, paymentStatus)
  const balanceDisplay = isNil(balance) || balance === 0 ? null : `(${formatMoney(balance)})`

  //we need to check if there are still pending payments
  const hasPendingPayment = find(payments, (e) => e.status === PaymentStatusEnum.pending)
  const showAwaitingAction =
    hasPendingPayment && [OrderPaymentStatusEnum.unpaid, OrderPaymentStatusEnum.partiallyPaid].includes(paymentStatus)

  // On-account orders stay in AR until genuinely settled, so the pill only shows
  // while the balance is still open (Unpaid / Partially Paid) on a credit term.
  const isOpenForCollection = [OrderPaymentStatusEnum.unpaid, OrderPaymentStatusEnum.partiallyPaid].includes(
    paymentStatus
  )
  const showOnAccount = isOpenForCollection && isOnAccountTerm(paymentTerms)

  return (
    <HorizontalStack gap={2}>
      <StatusColorBadge
        pill
        className="px-2"
        color={orderPaymentStatus}
        status={
          <HorizontalStack gap={2}>
            <ShowIf condition={!!paymentStatus}>
              <div>
                {paymentStatus}
                <ShowIf condition={!!showAwaitingAction}> - with pending payment.</ShowIf>
              </div>
            </ShowIf>
            <ShowIf condition={!!balanceDisplay}>
              <div>{balanceDisplay}</div>
            </ShowIf>
          </HorizontalStack>
        }
      />
      <ShowIf condition={showOnAccount}>
        {/* Subtle Bootstrap variant — theme-aware in both light and dark, using its own
            info-subtle styling, apart from the tone/custom-color paid/unpaid pill and the
            Inactive-tone Not Receivable badge rendered by StatusColorBadge. */}
        <span className="badge rounded-pill bg-info-subtle text-info-emphasis border border-info-subtle px-2 py-1">
          On Account
        </span>
      </ShowIf>
    </HorizontalStack>
  )
}
